/**
 * Returns API Client
 * Handles Pickware ERP Return Order API calls
 */

import BaseApiClient from './BaseApiClient';

const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class ReturnsApiClient {
    constructor() {
        this.baseClient = new BaseApiClient();
        this.api = this.baseClient.api;
    }

    /**
     * Find order by order number with line item associations
     * @param {string} orderNumber - Shopware order number
     * @returns {Object} Order data with lineItems
     */
    async findOrderByNumber(orderNumber) {
        try {
            const response = await this.api.post('/search/order', {
                filter: [
                    { type: 'equals', field: 'orderNumber', value: orderNumber }
                ],
                associations: {
                    lineItems: {}
                },
                includes: {
                    order: ['id', 'orderNumber', 'lineItems', 'taxStatus'],
                    order_line_item: ['id', 'productId', 'label', 'type', 'quantity', 'payload', 'price', 'unitPrice']
                },
                limit: 1
            });

            const orders = response.data?.data || [];
            if (orders.length === 0) {
                return { success: false, error: `Bestellung "${orderNumber}" nicht gefunden` };
            }

            if (DEBUG_API) {
                console.log(`[Returns API] Found order ${orderNumber}:`, orders[0].id);
            }

            return { success: true, data: orders[0] };
        } catch (error) {
            console.error(`[Returns API] Error finding order ${orderNumber}:`, error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get available Pickware ERP warehouses
     * @returns {Object} List of warehouses
     */
    async getWarehouses() {
        try {
            const response = await this.api.post('/search/pickware-erp-warehouse', {
                limit: 100,
                sort: [{ field: 'name', order: 'ASC' }]
            });

            const warehouses = response.data?.data || [];

            if (DEBUG_API) {
                console.log('[Returns API] Warehouses loaded:', warehouses.length);
            }

            return { success: true, data: warehouses };
        } catch (error) {
            console.error('[Returns API] Error loading warehouses:', error);
            return { success: false, data: [], error: error.message };
        }
    }

    /**
     * Find products by product numbers
     * @param {Array} productNumbers - Array of product numbers
     * @returns {Object} Map of productNumber → {id, productNumber, name}
     */
    async findProductsByNumbers(productNumbers) {
        try {
            const response = await this.api.post('/search/product', {
                filter: [
                    { type: 'equalsAny', field: 'productNumber', value: productNumbers }
                ],
                limit: productNumbers.length
            });

            const products = response.data?.data || [];
            const productMap = {};
            for (const p of products) {
                productMap[p.productNumber] = {
                    id: p.id,
                    productNumber: p.productNumber,
                    name: p.translated?.name || p.name || p.productNumber
                };
            }

            if (DEBUG_API) {
                console.log(`[Returns API] Found ${products.length} of ${productNumbers.length} products`);
            }

            return { success: true, data: productMap };
        } catch (error) {
            console.error('[Returns API] Error finding products:', error);
            return { success: false, data: {}, error: error.message };
        }
    }

    /**
     * Create completed return orders (all-in-one: request → approve → receive → complete + stock)
     * @param {Object} payload - Return orders payload
     * @returns {Object} Created return orders
     */
    async createCompletedReturnOrders(payload) {
        try {
            if (DEBUG_API) {
                console.log('[Returns API] Creating completed return orders:', JSON.stringify(payload, null, 2));
            }

            const response = await this.api.post(
                '/_action/pickware-erp/create-completed-return-orders',
                { ...payload, associations: payload.associations || {} }
            );

            return { success: true, data: response.data };
        } catch (error) {
            console.error('[Returns API] Error creating completed return orders:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Request and approve a return order (status: announced)
     * @param {Object} payload - Return order payload
     * @returns {Object} Created return order
     */
    async requestAndApproveReturnOrder(payload) {
        try {
            if (DEBUG_API) {
                console.log('[Returns API] Request & approve return order:', JSON.stringify(payload, null, 2));
            }

            const response = await this.api.post(
                '/_action/pickware-erp/request-and-approve-return-order',
                { ...payload, associations: payload.associations || {} }
            );

            return { success: true, data: response.data };
        } catch (error) {
            console.error('[Returns API] Error requesting/approving return order:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Request return order only (status: requested, not approved)
     * Uses the same endpoint as request-and-approve but writes entity directly via DAL
     * @param {Object} returnOrderPayload - Return order payload
     * @returns {Object} Created return order
     */
    async requestReturnOrder(returnOrderPayload) {
        try {
            if (DEBUG_API) {
                console.log('[Returns API] Request return order (no approve):', JSON.stringify(returnOrderPayload, null, 2));
            }

            // Write directly via Shopware Entity API - Pickware handles state via DAL subscriber
            const response = await this.api.post(
                '/pickware-erp-return-order',
                returnOrderPayload
            );

            return { success: true, data: response.data };
        } catch (error) {
            console.error('[Returns API] Error requesting return order:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Mark return order as received (announced → received)
     * @param {string} returnOrderId - Return order ID (32-char hex)
     * @returns {Object} Updated return order
     */
    async receiveReturnOrder(returnOrderId) {
        try {
            const response = await this.api.post(
                '/_action/pickware-erp/receive-return-order',
                { returnOrderId, associations: {} }
            );

            return { success: true, data: response.data };
        } catch (error) {
            console.error('[Returns API] Error receiving return order:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Complete return order with stock adjustments (received → completed)
     * @param {string} returnOrderId - Return order ID (32-char hex)
     * @param {Array} stockAdjustments - Array of {productId, restock, dispose}
     * @returns {Object} Completed return order
     */
    async completeReturnOrder(returnOrderId, stockAdjustments = []) {
        try {
            const response = await this.api.post(
                '/_action/pickware-erp/complete-return-order',
                { returnOrderId, stockAdjustments, associations: {} }
            );

            return { success: true, data: response.data };
        } catch (error) {
            console.error('[Returns API] Error completing return order:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }
    /**
     * Transition the refund state of a return order to "refunded"
     * @param {string} returnOrderId - Return order ID (32-char hex)
     * @returns {Object} Result
     */
    async markRefundAsCompleted(returnOrderId) {
        try {
            // 1. Find the refund entity for this return order
            const refundResp = await this.api.post('/search/pickware-erp-return-order-refund', {
                filter: [
                    { type: 'equals', field: 'returnOrderId', value: returnOrderId }
                ],
                limit: 1
            });

            const refund = refundResp.data?.data?.[0];
            if (!refund) {
                if (DEBUG_API) {
                    console.log('[Returns API] No refund entity found for return order', returnOrderId);
                }
                return { success: true, data: null, skipped: true };
            }

            // 2. Transition refund state machine to "refunded"
            const transitionResp = await this.api.post(
                `/_action/state-machine/pickware_erp_return_order_refund/${refund.id}/state/refund`,
                {}
            );

            if (DEBUG_API) {
                console.log('[Returns API] Refund marked as completed for return order', returnOrderId);
            }

            return { success: true, data: transitionResp.data };
        } catch (error) {
            console.error('[Returns API] Error marking refund as completed:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }
}

const returnsApiClient = new ReturnsApiClient();
export default returnsApiClient;
